import { useEffect, useState } from "react";
import { Form, Input, Modal, Tree, Typography } from "antd";
import type { DataNode } from "antd/es/tree";
import { SECTION_TREE, SectionNode } from "../../access/sections";
import { CmsRoleFormValues, CmsRoleInfo } from "./types";

type RoleModalProps = {
  open: boolean;
  saving: boolean;
  role?: CmsRoleInfo;
  onCancel: () => void;
  onSubmit: (values: CmsRoleFormValues) => Promise<boolean>;
};

const toTreeData = (nodes: SectionNode[]): DataNode[] =>
  nodes.map((node) => ({
    key: node.key,
    title: node.label,
    children: node.children ? toTreeData(node.children) : undefined,
  }));

const TREE_DATA = toTreeData(SECTION_TREE);

export const RoleModal = ({
  open,
  saving,
  role,
  onCancel,
  onSubmit,
}: RoleModalProps) => {
  const [form] = Form.useForm<CmsRoleFormValues>();
  const [checkedSections, setCheckedSections] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }
    form.setFieldsValue({
      name: role?.name || "",
      description: role?.description || "",
    });
    setCheckedSections(role?.sections || []);
  }, [form, open, role]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const ok = await onSubmit({ ...values, sections: checkedSections });
    if (ok) {
      form.resetFields();
    }
  };

  return (
    <Modal
      title={role ? "Редактировать роль" : "Создать роль"}
      open={open}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={saving}
      onOk={handleSubmit}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form<CmsRoleFormValues> form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: "Введите название роли" }]}
        >
          <Input placeholder="Например: Редактор новостей" />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input.TextArea rows={2} placeholder="Кому выдаётся и зачем" />
        </Form.Item>

        <Typography.Text strong>Доступ к разделам</Typography.Text>
        <div
          style={{
            marginTop: 8,
            maxHeight: 320,
            overflowY: "auto",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            padding: 8,
          }}
        >
          <Tree
            checkable
            selectable={false}
            defaultExpandAll={false}
            treeData={TREE_DATA}
            checkedKeys={checkedSections}
            onCheck={(checked) => {
              const keys = Array.isArray(checked) ? checked : checked.checked;
              // Храним только листовые ключи-роуты (group:* — синтетика контейнеров).
              setCheckedSections(
                keys.map(String).filter((key) => !key.startsWith("group:"))
              );
            }}
          />
        </div>
      </Form>
    </Modal>
  );
};
